import React, { useState, useEffect } from "react";

import {
  Plus,
  Trash2,
  Check,
  Edit3,
  Save,
  X,
  Moon,
  Sun,
  HelpCircle,
  List,
  GitBranch,
  FormInput,
  Type,
  CheckSquare,
  Circle,
  ChevronDown,
  Calendar,
  Clock,
  Star,
  FileText,
  ChevronRight,
  Home,
  Settings,
  Copy,
  Move,
  ArrowUp,
  ArrowDown,
  Palette,
  Target,
  CheckCircle,
  AlertCircle,
  Users,
  Timer,
  Tag,
  Hash,
  Zap,
  Filter,
  Eye,
  EyeOff,
  Lightbulb,
  Bell,
  Bookmark,
} from "lucide-react";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

// Import the admin session hook to get user's authorized stores
import { useAppSelector } from "features/config/hooks";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";

interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  isEditing: boolean;
  isConditional?: boolean;
  answer?: "yes" | "no" | null;
  dependsOn?: string;
  showOnAnswer?: "yes" | "no";
  subItems?: ChecklistItem[];
}

interface FormField {
  id: string;
  type:
    | "text"
    | "paragraph"
    | "multiple-choice"
    | "checkboxes"
    | "dropdown"
    | "date"
    | "time"
    | "rating";
  label: string;
  required: boolean;
  options?: string[];
  isEditing: boolean;
  value?: any;
}

interface DynamicForm {
  id: string;
  title: string;
  fields: FormField[];
  isEditingTitle: boolean;
  savedAt?: number;
  description?: string;
  visibleToStores?: string[]; // Array of store IDs that can see this form
}

interface Checklist {
  id: string;
  title: string;
  items: ChecklistItem[];
  isEditingTitle: boolean;
  type: "normal" | "conditional";
  savedAt?: number;
  description?: string;
  visibleToStores?: string[]; // Array of store IDs that can see this checklist
}

export default function ChecklistBuilder() {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [dynamicForms, setDynamicForms] = useState<DynamicForm[]>([]);
  const [savedChecklists, setSavedChecklists] = useState<Checklist[]>([]);
  const [savedForms, setSavedForms] = useState<DynamicForm[]>([]);
  const [newItemText, setNewItemText] = useState<{ [key: string]: string }>({});
  const [newSubItemText, setNewSubItemText] = useState<{
    [key: string]: string;
  }>({});
  const [subItemShowOn, setSubItemShowOn] = useState<{
    [key: string]: "yes" | "no";
  }>({});
  const [darkMode, setDarkMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showFieldTypeDialog, setShowFieldTypeDialog] = useState(false);
  const [currentFormId, setCurrentFormId] = useState<string>("");
  const [expandedCards, setExpandedCards] = useState<{
    [key: string]: boolean;
  }>({});
  const [loading, setLoading] = useState(false);
  const [formOriginalIds, setFormOriginalIds] = useState<{
    [key: string]: string;
  }>({});
  const [checklistOriginalIds, setChecklistOriginalIds] = useState<{
    [key: string]: string;
  }>({});

  // Visibility state management
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [currentVisibilityId, setCurrentVisibilityId] = useState<string>("");
  const [currentVisibilityType, setCurrentVisibilityType] = useState<
    "checklist" | "form"
  >("checklist");

  // View state - true for home/saved items, false for creation/editing
  const [isHomeView, setIsHomeView] = useState(true);

  // Get user's authorized stores from admin session
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const userStores =
    getAdminSessionState.data?.admin?.user_details?.stores || [];
  const storeIds = userStores.map((store) => store.store_id);

  // Check if user is admin (no store restrictions)
  const isAdmin = storeIds.length === 0;

  // Get all available stores for selection (admin sees all, regular users see only their authorized stores)
  const getAvailableStores = () => {
    if (isAdmin) {
      // For admin, we might need to fetch all stores from an API
      // For now, return a sample list since admin has access to all
      return [
        { store_id: "all", name: "All Stores" },
        { store_id: "1", name: "Taters SM MOA Gamepark" },
        { store_id: "2", name: "Taters BGC" },
        { store_id: "3", name: "Taters Makati" },
        // Add more stores as needed
      ];
    } else {
      // Regular users only see their authorized stores
      return userStores;
    }
  };

  // API Base URL
  const API_BASE_URL = `${REACT_APP_DOMAIN_URL}api`;

  // VISIBILITY MANAGEMENT FUNCTIONS
  const openVisibilityDialog = (id: string, type: "checklist" | "form") => {
    setCurrentVisibilityId(id);
    setCurrentVisibilityType(type);
    setShowVisibilityDialog(true);
  };

  const updateVisibility = (storeIds: string[]) => {
    if (currentVisibilityType === "checklist") {
      setChecklists((prev) =>
        prev.map((checklist) =>
          checklist.id === currentVisibilityId
            ? { ...checklist, visibleToStores: storeIds }
            : checklist
        )
      );
    } else {
      setDynamicForms((prev) =>
        prev.map((form) =>
          form.id === currentVisibilityId
            ? { ...form, visibleToStores: storeIds }
            : form
        )
      );
    }
    setShowVisibilityDialog(false);
    setCurrentVisibilityId("");
  };

  const getCurrentVisibilityStores = () => {
    if (currentVisibilityType === "checklist") {
      const checklist = checklists.find((c) => c.id === currentVisibilityId);
      return checklist?.visibleToStores || [];
    } else {
      const form = dynamicForms.find((f) => f.id === currentVisibilityId);
      return form?.visibleToStores || [];
    }
  };

  // DYNAMIC FORMS DATABASE FUNCTIONS
  const saveFormToDatabase = async (form: DynamicForm) => {
    try {
      setLoading(true);
      console.log("Saving form:", form);

      // Check if this form has an original ID (meaning it was loaded from database)
      const originalId = formOriginalIds[form.id];
      const isUpdate = !!originalId;

      // Prepare form data - include ALL fields (even if being edited)
      const formDataToSave = {
        title:
          form.title && form.title.trim().length > 0
            ? form.title.trim()
            : "Untitled Form",
        description: form.description || "",
        visibleToStores: form.visibleToStores || [], // Include visibility settings
        fields: form.fields.map((field) => ({
          id: field.id,
          type: field.type,
          label: field.label || "Untitled Field",
          required: Boolean(field.required),
          options: field.options || null,
          value: field.value !== undefined ? field.value : null,
          isEditing: false, // Always save as not editing
        })),
      };

      console.log("Form data being saved:", formDataToSave);
      console.log("Is update:", isUpdate, "Original ID:", originalId);

      // Use different endpoints for create vs update
      const url = isUpdate
        ? `${API_BASE_URL}/checklist_builder/update_form/${originalId}`
        : `${API_BASE_URL}/checklist_builder/save_form`;

      const method = isUpdate ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formDataToSave),
      });

      const result = await response.json();

      if (response.ok) {
        console.log(
          `Form ${isUpdate ? "updated" : "saved"} successfully:`,
          result.message
        );
        await loadSavedFormsFromDatabase(); // Refresh saved forms

        // Remove form from active forms and clean up tracking
        setDynamicForms((prev) => prev.filter((f) => f.id !== form.id));
        if (originalId) {
          setFormOriginalIds((prev) => {
            const newIds = { ...prev };
            delete newIds[form.id];
            return newIds;
          });
        }

        // Go back to home view after saving
        if (
          checklists.length === 0 &&
          dynamicForms.filter((f) => f.id !== form.id).length === 0
        ) {
          setIsHomeView(true);
        }
      } else {
        console.error(
          `Error ${isUpdate ? "updating" : "saving"} form:`,
          result.message
        );
        alert(
          `Error ${isUpdate ? "updating" : "saving"} form: ` + result.message
        );
      }
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Error saving form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadSavedFormsFromDatabase = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_forms`
      );
      const result = await response.json();

      if (response.ok) {
        console.log("Raw forms data from server:", result.data);

        const processedForms = result.data.map((form: any) => ({
          id: form.id.toString(),
          title: form.title || "Untitled Form",
          description: form.description || "",
          isEditingTitle: false,
          savedAt: new Date(form.saved_at || form.created_at).getTime(),
          visibleToStores: form.visible_to_stores || [], // Load visibility settings
          fields: (form.fields || []).map((field: any) => ({
            id: field.id ? field.id.toString() : Date.now().toString(),
            type: field.type || "text",
            label: field.label || "Untitled Field",
            required: Boolean(field.required),
            options: field.options
              ? Array.isArray(field.options)
                ? field.options
                : JSON.parse(field.options)
              : ["multiple-choice", "checkboxes", "dropdown"].includes(
                  field.type
                )
              ? []
              : null,
            isEditing: false,
            value:
              field.value !== undefined &&
              field.value !== null &&
              field.value !== ""
                ? typeof field.value === "string" && field.value.startsWith("[")
                  ? JSON.parse(field.value)
                  : field.value
                : field.type === "checkboxes"
                ? []
                : field.type === "rating"
                ? 0
                : "",
          })),
        }));

        console.log("Processed forms:", processedForms);
        setSavedForms(processedForms);
      } else {
        console.error("Error loading forms:", result.message);
      }
    } catch (error) {
      console.error("Error loading forms:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFormFromDatabase = async (formId: string) => {
    try {
      setLoading(true);
      console.log("Loading form with ID:", formId);

      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_form/${formId}`
      );
      const result = await response.json();

      if (response.ok) {
        console.log("Raw form data from server:", result.data);

        // Generate a new temporary ID for the working form
        const tempId = `loaded-${Date.now()}`;

        // Process the form data properly
        const loadedForm: DynamicForm = {
          id: tempId, // New temporary ID for loaded form
          title: result.data.title || "Untitled Form",
          description: result.data.description || "",
          isEditingTitle: false,
          visibleToStores: result.data.visible_to_stores || [], // Load visibility settings
          fields: (result.data.fields || []).map((field: any) => ({
            id: `field-${Date.now()}-${Math.random()}`, // New ID for loaded field
            type: field.type || "text",
            label: field.label || "Untitled Field",
            required: Boolean(field.required),
            options: field.options
              ? Array.isArray(field.options)
                ? field.options
                : JSON.parse(field.options)
              : ["multiple-choice", "checkboxes", "dropdown"].includes(
                  field.type
                )
              ? []
              : null,
            isEditing: false,
            value:
              field.value !== undefined &&
              field.value !== null &&
              field.value !== ""
                ? typeof field.value === "string" && field.value.startsWith("[")
                  ? JSON.parse(field.value)
                  : field.value
                : field.type === "checkboxes"
                ? []
                : field.type === "rating"
                ? 0
                : "",
          })),
        };

        console.log("Processed loaded form:", loadedForm);

        // Track the original form ID so we can update it later
        setFormOriginalIds((prev) => ({
          ...prev,
          [tempId]: formId, // Map temporary ID to original ID
        }));

        // Add the loaded form to active forms and switch to creation view
        setDynamicForms((prev) => [...prev, loadedForm]);
        setIsHomeView(false);
      } else {
        console.error("Error loading form:", result.message);
        alert("Error loading form: " + result.message);
      }
    } catch (error) {
      console.error("Error loading form:", error);
      alert("Error loading form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteFormFromDatabase = async (formId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/delete_form/${formId}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (response.ok) {
        console.log("Form deleted successfully:", result.message);
        await loadSavedFormsFromDatabase(); // Refresh saved forms
      } else {
        console.error("Error deleting form:", result.message);
        alert("Error deleting form: " + result.message);
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      alert("Error deleting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // CHECKLIST DATABASE FUNCTIONS
  const saveChecklistToDatabase = async (checklist: Checklist) => {
    try {
      setLoading(true);
      console.log("Saving checklist:", checklist);

      // Check if this checklist has an original ID (meaning it was loaded from database)
      const originalId = checklistOriginalIds[checklist.id];
      const isUpdate = !!originalId;

      // Prepare checklist data
      const checklistDataToSave = {
        title:
          checklist.title && checklist.title.trim().length > 0
            ? checklist.title.trim()
            : "Untitled Checklist",
        description: checklist.description || "",
        type: checklist.type,
        visibleToStores: checklist.visibleToStores || [], // Include visibility settings
        items: checklist.items.map((item) => ({
          id: item.id,
          text: item.text,
          completed: Boolean(item.completed),
          isConditional: Boolean(item.isConditional),
          answer: item.answer || null,
          dependsOn: item.dependsOn || null,
          showOnAnswer: item.showOnAnswer || null,
          subItems: item.subItems || [],
        })),
      };

      console.log("Checklist data being saved:", checklistDataToSave);
      console.log("Is update:", isUpdate, "Original ID:", originalId);

      // Use different endpoints for create vs update
      const url = isUpdate
        ? `${API_BASE_URL}/checklist_builder/update_checklist/${originalId}`
        : `${API_BASE_URL}/checklist_builder/save_checklist`;

      const method = isUpdate ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(checklistDataToSave),
      });

      const result = await response.json();

      if (response.ok) {
        console.log(
          `Checklist ${isUpdate ? "updated" : "saved"} successfully:`,
          result.message
        );
        await loadSavedChecklistsFromDatabase(); // Refresh saved checklists

        // Remove checklist from active checklists and clean up tracking
        setChecklists((prev) => prev.filter((c) => c.id !== checklist.id));
        if (originalId) {
          setChecklistOriginalIds((prev) => {
            const newIds = { ...prev };
            delete newIds[checklist.id];
            return newIds;
          });
        }

        // Go back to home view after saving
        if (
          dynamicForms.length === 0 &&
          checklists.filter((c) => c.id !== checklist.id).length === 0
        ) {
          setIsHomeView(true);
        }
      } else {
        console.error(
          `Error ${isUpdate ? "updating" : "saving"} checklist:`,
          result.message
        );
        alert(
          `Error ${isUpdate ? "updating" : "saving"} checklist: ` +
            result.message
        );
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      alert("Error saving checklist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadSavedChecklistsFromDatabase = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_checklists`
      );
      const result = await response.json();

      if (response.ok) {
        console.log("Raw checklists data from server:", result.data);

        const processedChecklists = result.data.map((checklist: any) => ({
          id: checklist.id.toString(),
          title: checklist.title || "Untitled Checklist",
          description: checklist.description || "",
          type: checklist.type || "normal",
          isEditingTitle: false,
          savedAt: new Date(
            checklist.saved_at || checklist.created_at
          ).getTime(),
          visibleToStores: checklist.visible_to_stores || [], // Load visibility settings
          items: checklist.items || [],
        }));

        console.log("Processed checklists:", processedChecklists);
        setSavedChecklists(processedChecklists);
      } else {
        console.error("Error loading checklists:", result.message);
      }
    } catch (error) {
      console.error("Error loading checklists:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadChecklistFromDatabase = async (checklistId: string) => {
    try {
      setLoading(true);
      console.log("Loading checklist with ID:", checklistId);

      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_checklist/${checklistId}`
      );
      const result = await response.json();

      if (response.ok) {
        console.log("Raw checklist data from server:", result.data);

        // Generate a new temporary ID for the working checklist
        const tempId = `loaded-checklist-${Date.now()}`;

        // Process the checklist data properly
        const loadedChecklist: Checklist = {
          id: tempId,
          title: result.data.title || "Untitled Checklist",
          description: result.data.description || "",
          type: result.data.type || "normal",
          isEditingTitle: false,
          visibleToStores: result.data.visible_to_stores || [], // Load visibility settings
          items: result.data.items || [],
        };

        console.log("Processed loaded checklist:", loadedChecklist);

        // Track the original checklist ID so we can update it later
        setChecklistOriginalIds((prev) => ({
          ...prev,
          [tempId]: checklistId,
        }));

        // Add the loaded checklist to active checklists and switch to creation view
        setChecklists((prev) => [...prev, loadedChecklist]);
        setIsHomeView(false);
      } else {
        console.error("Error loading checklist:", result.message);
        alert("Error loading checklist: " + result.message);
      }
    } catch (error) {
      console.error("Error loading checklist:", error);
      alert("Error loading checklist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteChecklistFromDatabase = async (checklistId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/delete_checklist/${checklistId}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (response.ok) {
        console.log("Checklist deleted successfully:", result.message);
        await loadSavedChecklistsFromDatabase(); // Refresh saved checklists
      } else {
        console.error("Error deleting checklist:", result.message);
        alert("Error deleting checklist: " + result.message);
      }
    } catch (error) {
      console.error("Error deleting checklist:", error);
      alert("Error deleting checklist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load saved forms and checklists on component mount
  useEffect(() => {
    loadSavedFormsFromDatabase();
    loadSavedChecklistsFromDatabase();
  }, []);

  // CORE FORM MANAGEMENT FUNCTIONS
  const createNewChecklist = (type: "normal" | "conditional" | "dynamic") => {
    if (type === "dynamic") {
      const newForm: DynamicForm = {
        id: `form-${Date.now()}`,
        title: "New Dynamic Form",
        fields: [],
        isEditingTitle: true,
        description: "",
        visibleToStores: [], // Change this to empty array for no default selection
      };
      setDynamicForms([...dynamicForms, newForm]);
      setShowDialog(false);
      setIsHomeView(false); // Switch to creation view
      return;
    }

    const newChecklist: Checklist = {
      id: `checklist-${Date.now()}`,
      title: type === "normal" ? "New Checklist" : "New Conditional Checklist",
      items: [],
      isEditingTitle: true,
      type,
      visibleToStores: [], // Change this to empty array for no default selection
    };
    setChecklists([...checklists, newChecklist]);
    setShowDialog(false);
    setIsHomeView(false); // Switch to creation view
  };

  const saveChecklist = (checklist: Checklist) => {
    saveChecklistToDatabase(checklist);
  };

  const deleteSavedChecklist = (checklistId: string) => {
    deleteChecklistFromDatabase(checklistId);
  };

  const deleteSavedForm = (formId: string) => {
    deleteFormFromDatabase(formId);
  };

  const loadSavedChecklist = (checklist: Checklist) => {
    loadChecklistFromDatabase(checklist.id);
  };

  const loadSavedForm = (form: DynamicForm) => {
    loadFormFromDatabase(form.id);
  };

  const deleteChecklist = (checklistId: string) => {
    const updatedChecklists = checklists.filter((c) => c.id !== checklistId);
    setChecklists(updatedChecklists);

    // If no more active items, go back to home view
    if (updatedChecklists.length === 0 && dynamicForms.length === 0) {
      setIsHomeView(true);
    }
  };

  const deleteForm = (formId: string) => {
    const updatedForms = dynamicForms.filter((f) => f.id !== formId);
    setDynamicForms(updatedForms);

    // If no more active items, go back to home view
    if (checklists.length === 0 && updatedForms.length === 0) {
      setIsHomeView(true);
    }
  };

  const duplicateChecklist = (checklist: Checklist) => {
    const duplicated: Checklist = {
      ...checklist,
      id: `checklist-${Date.now()}`,
      title: `${checklist.title} (Copy)`,
      items: checklist.items.map((item) => ({
        ...item,
        id: `item-${Date.now()}-${Math.random()}`,
        completed: false,
        subItems: item.subItems?.map((sub) => ({
          ...sub,
          id: `subitem-${Date.now()}-${Math.random()}`,
          completed: false,
        })),
      })),
    };
    setChecklists([...checklists, duplicated]);
    setIsHomeView(false); // Switch to creation view
  };

  const goToHome = () => {
    setIsHomeView(true);
  };

  // FORM FIELD MANAGEMENT FUNCTIONS
  const addField = (formId: string, fieldType: FormField["type"]) => {
    const newField: FormField = {
      id: `field-${Date.now()}-${Math.random()}`,
      type: fieldType,
      label: `New ${fieldType} field`,
      required: false,
      isEditing: true,
      options: ["multiple-choice", "checkboxes", "dropdown"].includes(fieldType)
        ? ["Option 1"]
        : undefined,
      value: fieldType === "checkboxes" ? [] : fieldType === "rating" ? 0 : "",
    };

    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? { ...form, fields: [...form.fields, newField] }
          : form
      )
    );
    setShowFieldTypeDialog(false);
    setCurrentFormId("");
  };

  const updateField = (
    formId: string,
    fieldId: string,
    updates: Partial<FormField>
  ) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              fields: form.fields.map((field) =>
                field.id === fieldId ? { ...field, ...updates } : field
              ),
            }
          : form
      )
    );
  };

  const deleteField = (formId: string, fieldId: string) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              fields: form.fields.filter((field) => field.id !== fieldId),
            }
          : form
      )
    );
  };

  const addOption = (formId: string, fieldId: string) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              fields: form.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: [
                        ...(field.options || []),
                        `Option ${(field.options?.length || 0) + 1}`,
                      ],
                    }
                  : field
              ),
            }
          : form
      )
    );
  };

  const updateOption = (
    formId: string,
    fieldId: string,
    optionIndex: number,
    value: string
  ) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              fields: form.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: field.options?.map((option, index) =>
                        index === optionIndex ? value : option
                      ),
                    }
                  : field
              ),
            }
          : form
      )
    );
  };

  const deleteOption = (
    formId: string,
    fieldId: string,
    optionIndex: number
  ) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              fields: form.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: field.options?.filter(
                        (_, index) => index !== optionIndex
                      ),
                    }
                  : field
              ),
            }
          : form
      )
    );
  };

  const updateFormTitle = (formId: string, title: string) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId ? { ...form, title, isEditingTitle: false } : form
      )
    );
  };

  const toggleFormTitleEdit = (formId: string) => {
    setDynamicForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? { ...form, isEditingTitle: !form.isEditingTitle }
          : form
      )
    );
  };

  // CHECKLIST FUNCTIONS (simplified)
  const updateChecklistTitle = (checklistId: string, title: string) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId ? { ...c, title, isEditingTitle: false } : c
      )
    );
  };

  const toggleTitleEdit = (checklistId: string) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId ? { ...c, isEditingTitle: !c.isEditingTitle } : c
      )
    );
  };

  const addItem = (
    checklistId: string,
    text: string,
    isConditional: boolean = false
  ) => {
    if (!text.trim()) return;

    const newItem: ChecklistItem = {
      id: `item-${Date.now()}-${Math.random()}`,
      text: text.trim(),
      completed: false,
      isEditing: false,
      isConditional,
      answer: isConditional ? null : undefined,
      subItems: isConditional ? [] : undefined,
    };

    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId ? { ...c, items: [...c.items, newItem] } : c
      )
    );
    setNewItemText({ ...newItemText, [checklistId]: "" });
  };

  const addSubItem = (checklistId: string, parentItemId: string) => {
    const text = newSubItemText[`${checklistId}-${parentItemId}`]?.trim();
    const showOn = subItemShowOn[`${checklistId}-${parentItemId}`] || "yes";
    if (!text) return;

    const newSubItem: ChecklistItem = {
      id: `subitem-${Date.now()}-${Math.random()}`,
      text,
      completed: false,
      isEditing: false,
      dependsOn: parentItemId,
      showOnAnswer: showOn,
    };

    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: c.items.map((item) =>
                item.id === parentItemId
                  ? {
                      ...item,
                      subItems: [...(item.subItems || []), newSubItem],
                    }
                  : item
              ),
            }
          : c
      )
    );
    setNewSubItemText({
      ...newSubItemText,
      [`${checklistId}-${parentItemId}`]: "",
    });
    setSubItemShowOn({
      ...subItemShowOn,
      [`${checklistId}-${parentItemId}`]: "yes",
    });
  };

  const deleteItem = (
    checklistId: string,
    itemId: string,
    parentItemId?: string
  ) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: parentItemId
                ? c.items.map((item) =>
                    item.id === parentItemId
                      ? {
                          ...item,
                          subItems: item.subItems?.filter(
                            (sub) => sub.id !== itemId
                          ),
                        }
                      : item
                  )
                : c.items.filter((item) => item.id !== itemId),
            }
          : c
      )
    );
  };

  const toggleItem = (
    checklistId: string,
    itemId: string,
    parentItemId?: string
  ) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: parentItemId
                ? c.items.map((item) =>
                    item.id === parentItemId
                      ? {
                          ...item,
                          subItems: item.subItems?.map((sub) =>
                            sub.id === itemId
                              ? { ...sub, completed: !sub.completed }
                              : sub
                          ),
                        }
                      : item
                  )
                : c.items.map((item) =>
                    item.id === itemId
                      ? { ...item, completed: !item.completed }
                      : item
                  ),
            }
          : c
      )
    );
  };

  const setConditionalAnswer = (
    checklistId: string,
    itemId: string,
    answer: "yes" | "no"
  ) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: c.items.map((item) =>
                item.id === itemId ? { ...item, answer } : item
              ),
            }
          : c
      )
    );
  };

  const toggleItemEdit = (
    checklistId: string,
    itemId: string,
    parentItemId?: string
  ) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: parentItemId
                ? c.items.map((item) =>
                    item.id === parentItemId
                      ? {
                          ...item,
                          subItems: item.subItems?.map((sub) =>
                            sub.id === itemId
                              ? { ...sub, isEditing: !sub.isEditing }
                              : sub
                          ),
                        }
                      : item
                  )
                : c.items.map((item) =>
                    item.id === itemId
                      ? { ...item, isEditing: !item.isEditing }
                      : item
                  ),
            }
          : c
      )
    );
  };

  const updateItem = (
    checklistId: string,
    itemId: string,
    text: string,
    parentItemId?: string
  ) => {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: parentItemId
                ? c.items.map((item) =>
                    item.id === parentItemId
                      ? {
                          ...item,
                          subItems: item.subItems?.map((sub) =>
                            sub.id === itemId
                              ? { ...sub, text, isEditing: false }
                              : sub
                          ),
                        }
                      : item
                  )
                : c.items.map((item) =>
                    item.id === itemId
                      ? { ...item, text, isEditing: false }
                      : item
                  ),
            }
          : c
      )
    );
  };

  // UTILITY FUNCTIONS
  const getProgress = (checklist: Checklist) => {
    if (checklist.items.length === 0) return 0;

    if (checklist.type === "conditional") {
      let totalItems = 0;
      let answeredItems = 0;

      checklist.items.forEach((item) => {
        if (item.isConditional) {
          totalItems++;
          if (item.answer !== null) {
            answeredItems++;
          }
        }
      });

      return totalItems === 0 ? 0 : (answeredItems / totalItems) * 100;
    }

    return (
      (checklist.items.filter((item) => item.completed).length /
        checklist.items.length) *
      100
    );
  };

  const getVisibleItems = (checklist: Checklist) => {
    if (checklist.type === "normal") return checklist.items;

    return checklist.items.map((item) => ({
      ...item,
      subItems: item.subItems?.filter((subItem) => {
        if (!subItem.dependsOn) return true;
        const parentItem = checklist.items.find(
          (i) => i.id === subItem.dependsOn
        );
        if (!parentItem || !parentItem.isConditional) return true;
        return parentItem.answer === subItem.showOnAnswer;
      }),
    }));
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getFieldIcon = (type: FormField["type"]) => {
    switch (type) {
      case "text":
        return <Type className="w-6 h-6" />;
      case "paragraph":
        return <FileText className="w-6 h-6" />;
      case "multiple-choice":
        return <Circle className="w-6 h-6" />;
      case "checkboxes":
        return <CheckSquare className="w-6 h-6" />;
      case "dropdown":
        return <ChevronDown className="w-6 h-6" />;
      case "date":
        return <Calendar className="w-6 h-6" />;
      case "time":
        return <Clock className="w-6 h-6" />;
      case "rating":
        return <Star className="w-6 h-6" />;
      default:
        return <FormInput className="w-6 h-6" />;
    }
  };

  // Get store name by ID
  // Fix just the TypeScript error in getStoreName function
  const getStoreName = (storeId: string): string => {
    if (storeId === "all") return "All Stores";
    const availableStores = getAvailableStores();

    // Type assertion to handle the union type - this is the minimal fix
    const store = (availableStores as any[]).find(
      (s: any) => s.store_id.toString() === storeId
    );
    return store?.name || `Store ${storeId}`;
  };

  // RENDER FUNCTIONS
  const renderItem = (
    checklist: Checklist,
    item: ChecklistItem,
    parentItemId?: string
  ) => {
    return (
      <div key={item.id}>
        <div
          className={`p-4 rounded-lg transition-all duration-300 border ${
            darkMode
              ? "hover:bg-white/5 border-white/10 hover:border-white/20"
              : "hover:bg-gray-50 border-gray-200 hover:border-gray-300"
          } ${item.completed ? "opacity-60" : ""}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              {item.isConditional ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setConditionalAnswer(checklist.id, item.id, "yes")
                    }
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      item.answer === "yes"
                        ? "bg-green-500 text-white"
                        : darkMode
                        ? "bg-white/10 text-white/70 hover:bg-green-500/20"
                        : "bg-gray-200 text-gray-600 hover:bg-green-100"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() =>
                      setConditionalAnswer(checklist.id, item.id, "no")
                    }
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      item.answer === "no"
                        ? "bg-red-500 text-white"
                        : darkMode
                        ? "bg-white/10 text-white/70 hover:bg-red-500/20"
                        : "bg-gray-200 text-gray-600 hover:bg-red-100"
                    }`}
                  >
                    No
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() =>
                      toggleItem(checklist.id, item.id, parentItemId)
                    }
                    className="hidden"
                  />
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-all duration-200 ${
                      item.completed
                        ? "bg-green-500 border-green-500"
                        : darkMode
                        ? "bg-transparent border-white/50 hover:border-green-400"
                        : "bg-transparent border-gray-300 hover:border-green-400"
                    }`}
                  >
                    {item.completed && <Check className="w-3 h-3 text-white" />}
                  </span>
                </label>
              )}

              <div className="flex-1">
                {item.isEditing ? (
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) =>
                      setChecklists((prev) =>
                        prev.map((c) =>
                          c.id === checklist.id
                            ? {
                                ...c,
                                items: parentItemId
                                  ? c.items.map((i) =>
                                      i.id === parentItemId
                                        ? {
                                            ...i,
                                            subItems: i.subItems?.map((sub) =>
                                              sub.id === item.id
                                                ? {
                                                    ...sub,
                                                    text: e.target.value,
                                                  }
                                                : sub
                                            ),
                                          }
                                        : i
                                    )
                                  : c.items.map((i) =>
                                      i.id === item.id
                                        ? { ...i, text: e.target.value }
                                        : i
                                    ),
                              }
                            : c
                        )
                      )
                    }
                    onBlur={() =>
                      updateItem(checklist.id, item.id, item.text, parentItemId)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        updateItem(
                          checklist.id,
                          item.id,
                          item.text,
                          parentItemId
                        );
                      }
                    }}
                    className={`w-full bg-transparent border-b border-orange-400 focus:outline-none focus:border-red-400 transition-colors text-lg font-medium ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                    autoFocus
                  />
                ) : (
                  <span
                    className={`font-medium ${
                      item.completed
                        ? "line-through text-gray-400"
                        : darkMode
                        ? "text-white"
                        : "text-gray-800"
                    }`}
                  >
                    {item.text}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-1">
              {!item.isEditing && (
                <>
                  <button
                    onClick={() =>
                      toggleItemEdit(checklist.id, item.id, parentItemId)
                    }
                    className={`p-1 rounded transition-colors ${
                      darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                    }`}
                  >
                    <Edit3 className="w-4 h-4 text-orange-400" />
                  </button>

                  <button
                    onClick={() =>
                      deleteItem(checklist.id, item.id, parentItemId)
                    }
                    className={`p-1 rounded transition-colors ${
                      darkMode ? "hover:bg-red-500/20" : "hover:bg-red-100"
                    }`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {item.isConditional && (
          <div className="ml-8 mt-3">
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newSubItemText[`${checklist.id}-${item.id}`] || ""}
                onChange={(e) =>
                  setNewSubItemText({
                    ...newSubItemText,
                    [`${checklist.id}-${item.id}`]: e.target.value,
                  })
                }
                placeholder="Add a sub-task"
                className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-orange-400 transition-all ${
                  darkMode
                    ? "bg-white/10 border-white/20 text-white placeholder-white/50"
                    : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"
                }`}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addSubItem(checklist.id, item.id);
                  }
                }}
              />
              <select
                value={subItemShowOn[`${checklist.id}-${item.id}`] || "yes"}
                onChange={(e) =>
                  setSubItemShowOn({
                    ...subItemShowOn,
                    [`${checklist.id}-${item.id}`]: e.target.value as
                      | "yes"
                      | "no",
                  })
                }
                className={`px-3 py-2 rounded-lg border focus:outline-none focus:border-orange-400 transition-all ${
                  darkMode
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-white border-gray-200 text-gray-800"
                }`}
              >
                <option value="yes">Show on Yes</option>
                <option value="no">Show on No</option>
              </select>
              <button
                onClick={() => addSubItem(checklist.id, item.id)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  darkMode
                    ? "bg-orange-500 text-white hover:bg-orange-600"
                    : "bg-orange-500 text-white hover:bg-orange-600"
                }`}
              >
                Add Sub-task
              </button>
            </div>
            {item.subItems?.map((subItem) =>
              item.answer === subItem.showOnAnswer
                ? renderItem(checklist, subItem, item.id)
                : null
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFormField = (form: DynamicForm, field: FormField) => {
    return (
      <div
        key={field.id}
        className={`p-4 rounded-lg border transition-all duration-300 ${
          darkMode ? "border-white/20 bg-white/5" : "border-gray-200 bg-gray-50"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          {field.isEditing ? (
            <div className="flex-1 flex items-center gap-3">
              <input
                type="text"
                value={field.label}
                onChange={(e) =>
                  updateField(form.id, field.id, { label: e.target.value })
                }
                className={`flex-1 bg-transparent border-b border-orange-400 focus:outline-none focus:border-red-400 transition-colors ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
                autoFocus
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) =>
                    updateField(form.id, field.id, {
                      required: e.target.checked,
                    })
                  }
                  className="rounded"
                />
                <span
                  className={`text-sm ${
                    darkMode ? "text-white/70" : "text-gray-600"
                  }`}
                >
                  Required
                </span>
              </label>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className={`font-medium ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                {field.label}
              </span>
              {field.required && (
                <span className="text-red-500 text-sm">*</span>
              )}
              <span
                className={`text-xs px-2 py-1 rounded ${
                  darkMode
                    ? "bg-white/10 text-white/70"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {field.type}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() =>
                updateField(form.id, field.id, { isEditing: !field.isEditing })
              }
              className={`p-1 rounded transition-colors ${
                darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
              }`}
            >
              <Edit3 className="w-4 h-4 text-orange-400" />
            </button>
            <button
              onClick={() => deleteField(form.id, field.id)}
              className={`p-1 rounded transition-colors ${
                darkMode ? "hover:bg-red-500/20" : "hover:bg-red-100"
              }`}
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </div>

        {/* Field Options for Multiple Choice, Checkboxes, Dropdown */}
        {field.isEditing &&
          ["multiple-choice", "checkboxes", "dropdown"].includes(
            field.type
          ) && (
            <div className="space-y-2 mb-3">
              <label
                className={`text-sm font-medium ${
                  darkMode ? "text-white/80" : "text-gray-700"
                }`}
              >
                Options:
              </label>
              {field.options?.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) =>
                      updateOption(form.id, field.id, index, e.target.value)
                    }
                    className={`flex-1 px-3 py-2 rounded border focus:outline-none focus:border-orange-400 transition-all ${
                      darkMode
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                  />
                  <button
                    onClick={() => deleteOption(form.id, field.id, index)}
                    className={`p-2 rounded transition-colors ${
                      darkMode ? "hover:bg-red-500/20" : "hover:bg-red-100"
                    }`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addOption(form.id, field.id)}
                className={`px-3 py-2 rounded transition-colors text-sm ${
                  darkMode
                    ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                }`}
              >
                Add Option
              </button>
            </div>
          )}

        {/* Field Preview/Input */}
        <div className="mt-3">
          {field.type === "text" && (
            <input
              type="text"
              placeholder="Text input"
              value={field.value || ""}
              onChange={(e) =>
                updateField(form.id, field.id, { value: e.target.value })
              }
              className={`w-full px-3 py-2 rounded border ${
                darkMode
                  ? "bg-white/10 border-white/20 text-white placeholder-white/50"
                  : "bg-white border-gray-300 text-gray-800 placeholder-gray-400"
              }`}
            />
          )}
          {field.type === "paragraph" && (
            <textarea
              placeholder="Paragraph text"
              value={field.value || ""}
              onChange={(e) =>
                updateField(form.id, field.id, { value: e.target.value })
              }
              rows={3}
              className={`w-full px-3 py-2 rounded border resize-none ${
                darkMode
                  ? "bg-white/10 border-white/20 text-white placeholder-white/50"
                  : "bg-white border-gray-300 text-gray-800 placeholder-gray-400"
              }`}
            />
          )}
          {field.type === "multiple-choice" && (
            <div className="space-y-2">
              {field.options?.map((option, index) => (
                <label key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`radio-${field.id}`}
                    checked={field.value === option}
                    onChange={() =>
                      updateField(form.id, field.id, { value: option })
                    }
                  />
                  <span
                    className={darkMode ? "text-white/80" : "text-gray-700"}
                  >
                    {option}
                  </span>
                </label>
              ))}
            </div>
          )}
          {field.type === "checkboxes" && (
            <div className="space-y-2">
              {field.options?.map((option, index) => (
                <label key={index} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      Array.isArray(field.value)
                        ? field.value.includes(option)
                        : false
                    }
                    onChange={(e) => {
                      const currentValues = Array.isArray(field.value)
                        ? field.value
                        : [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter((v) => v !== option);
                      updateField(form.id, field.id, { value: newValues });
                    }}
                  />
                  <span
                    className={darkMode ? "text-white/80" : "text-gray-700"}
                  >
                    {option}
                  </span>
                </label>
              ))}
            </div>
          )}
          {field.type === "dropdown" && (
            <select
              value={field.value || ""}
              onChange={(e) =>
                updateField(form.id, field.id, { value: e.target.value })
              }
              className={`w-full px-3 py-2 rounded border ${
                darkMode
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
            >
              <option value="">Select an option</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}
          {field.type === "date" && (
            <input
              type="date"
              value={field.value || ""}
              onChange={(e) =>
                updateField(form.id, field.id, { value: e.target.value })
              }
              className={`w-full px-3 py-2 rounded border ${
                darkMode
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
            />
          )}
          {field.type === "time" && (
            <input
              type="time"
              value={field.value || ""}
              onChange={(e) =>
                updateField(form.id, field.id, { value: e.target.value })
              }
              className={`w-full px-3 py-2 rounded border ${
                darkMode
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
            />
          )}
          {field.type === "rating" && (
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-6 h-6 cursor-pointer transition-colors ${
                    field.value && field.value >= star
                      ? "text-yellow-400 fill-current"
                      : "text-gray-300 hover:text-yellow-400"
                  }`}
                  onClick={() =>
                    updateField(form.id, field.id, { value: star })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen transition-all duration-500 p-6 ${
        darkMode
          ? "bg-gradient-to-br from-black via-gray-900 to-red-900"
          : "bg-gradient-to-br from-orange-50 via-white to-red-50"
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`p-8 rounded-2xl ${
                darkMode ? "bg-gray-900" : "bg-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                <span
                  className={`${darkMode ? "text-white" : "text-gray-800"}`}
                >
                  Processing...
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Theme Toggle and Home Button */}
        <div className="flex justify-between mb-4">
          <div>
            {!isHomeView && (
              <button
                onClick={goToHome}
                className={`p-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                  darkMode
                    ? "bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg hover:shadow-xl"
                    : "bg-gradient-to-r from-gray-800 to-black text-white shadow-lg hover:shadow-xl"
                }`}
              >
                <Home className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
              darkMode
                ? "bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg hover:shadow-xl"
                : "bg-gradient-to-r from-gray-800 to-black text-white shadow-lg hover:shadow-xl"
            }`}
          >
            {darkMode ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-5xl font-bold bg-gradient-to-r from-orange-400 via-red-500 to-orange-600 bg-clip-text text-transparent mb-4"
            style={{ fontFamily: '"Bebas Neue", sans-serif' }}
          >
            Checklist Builder
          </h1>
          <p
            className={`text-lg transition-colors duration-300 ${
              darkMode ? "text-white/70" : "text-gray-600"
            }`}
          >
            Create and manage your tasks with style
          </p>
        </div>

        {/* HOME VIEW - Saved Items and Create Button */}
        {isHomeView && (
          <>
            {/* Create New Checklist Button */}
            <div className="text-center mb-8">
              <button
                onClick={() => setShowDialog(true)}
                className={`group relative px-8 py-4 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${
                  darkMode
                    ? "bg-gradient-to-r from-orange-500 to-red-600 text-white border border-orange-400/30"
                    : "bg-gradient-to-r from-orange-500 to-red-600 text-white border border-orange-400/50 shadow-orange-200/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                  Create New Checklist
                </div>
              </button>
            </div>

            {/* Saved Items Section */}
            {(savedChecklists.length > 0 || savedForms.length > 0) && (
              <div className="mb-8">
                <h2
                  className={`text-2xl font-bold mb-4 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Saved Items
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Saved Checklists */}
                  {savedChecklists.map((checklist) => (
                    <div
                      key={checklist.id}
                      className={`p-6 rounded-xl border transition-all duration-300 hover:shadow-lg ${
                        darkMode
                          ? "bg-white/5 border-white/20 hover:border-white/30"
                          : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2 rounded-lg ${
                              checklist.type === "conditional"
                                ? darkMode
                                  ? "bg-red-500/20"
                                  : "bg-red-100"
                                : darkMode
                                ? "bg-orange-500/20"
                                : "bg-orange-100"
                            }`}
                          >
                            {checklist.type === "conditional" ? (
                              <GitBranch
                                className={`w-5 h-5 ${
                                  checklist.type === "conditional"
                                    ? "text-red-500"
                                    : "text-orange-500"
                                }`}
                              />
                            ) : (
                              <List className="w-5 h-5 text-orange-500" />
                            )}
                          </div>
                          <div>
                            <h3
                              className={`font-semibold ${
                                darkMode ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {checklist.title}
                            </h3>
                            {checklist.savedAt && (
                              <p
                                className={`text-xs ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                Saved: {formatDate(checklist.savedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadSavedChecklist(checklist)}
                            className={`p-2 rounded-lg transition-colors ${
                              darkMode
                                ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => duplicateChecklist(checklist)}
                            className={`p-2 rounded-lg transition-colors ${
                              darkMode
                                ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteSavedChecklist(checklist.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              darkMode
                                ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                : "bg-red-100 text-red-600 hover:bg-red-200"
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        {checklist.items?.length || 0} items • {checklist.type}{" "}
                        checklist
                      </p>
                      {/* Visibility indicator */}
                      {checklist.visibleToStores &&
                        checklist.visibleToStores.length > 0 && (
                          <div className="mt-2 flex items-center gap-1">
                            <Eye className="w-3 h-3 text-gray-400" />
                            <span
                              className={`text-xs ${
                                darkMode ? "text-white/50" : "text-gray-500"
                              }`}
                            >
                              Visible to:{" "}
                              {checklist.visibleToStores.includes("all")
                                ? "All Stores"
                                : `${checklist.visibleToStores.length} store(s)`}
                            </span>
                          </div>
                        )}
                    </div>
                  ))}

                  {/* Saved Forms */}
                  {savedForms.map((form) => (
                    <div
                      key={form.id}
                      className={`p-6 rounded-xl border transition-all duration-300 hover:shadow-lg ${
                        darkMode
                          ? "bg-white/5 border-white/20 hover:border-white/30"
                          : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2 rounded-lg ${
                              darkMode ? "bg-blue-500/20" : "bg-blue-100"
                            }`}
                          >
                            <FormInput className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <h3
                              className={`font-semibold ${
                                darkMode ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {form.title}
                            </h3>
                            {form.savedAt && (
                              <p
                                className={`text-xs ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                Saved: {formatDate(form.savedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadSavedForm(form)}
                            className={`p-2 rounded-lg transition-colors ${
                              darkMode
                                ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteSavedForm(form.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              darkMode
                                ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                : "bg-red-100 text-red-600 hover:bg-red-200"
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        {form.fields?.length || 0} fields • Dynamic form
                      </p>
                      {/* Visibility indicator */}
                      {form.visibleToStores &&
                        form.visibleToStores.length > 0 && (
                          <div className="mt-2 flex items-center gap-1">
                            <Eye className="w-3 h-3 text-gray-400" />
                            <span
                              className={`text-xs ${
                                darkMode ? "text-white/50" : "text-gray-500"
                              }`}
                            >
                              Visible to:{" "}
                              {form.visibleToStores.includes("all")
                                ? "All Stores"
                                : `${form.visibleToStores.length} store(s)`}
                            </span>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State for Home View */}
            {savedChecklists.length === 0 && savedForms.length === 0 && (
              <div
                className={`text-center py-16 ${
                  darkMode ? "text-white/50" : "text-gray-400"
                }`}
              >
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto mb-6 opacity-20">
                    <List className="w-full h-full" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">
                    No saved items yet
                  </h3>
                  <p className="text-lg">
                    Create your first checklist to get started!
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* CREATION/EDITING VIEW - Active Checklists and Forms */}
        {!isHomeView && (
          <>
            {/* Active Checklists */}
            {checklists.map((checklist) => (
              <div
                key={checklist.id}
                className={`mb-8 p-8 rounded-2xl border transition-all duration-300 hover:shadow-lg ${
                  darkMode
                    ? "bg-gradient-to-br from-white/5 to-white/10 border-white/20 backdrop-blur-sm"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                {/* Checklist Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4 flex-1">
                    {checklist.isEditingTitle ? (
                      <input
                        type="text"
                        value={checklist.title}
                        onChange={(e) =>
                          setChecklists((prev) =>
                            prev.map((c) =>
                              c.id === checklist.id
                                ? { ...c, title: e.target.value }
                                : c
                            )
                          )
                        }
                        onBlur={() =>
                          updateChecklistTitle(checklist.id, checklist.title)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            updateChecklistTitle(checklist.id, checklist.title);
                          }
                        }}
                        className={`text-3xl font-bold bg-transparent border-b-2 border-orange-400 focus:outline-none focus:border-red-400 transition-colors ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                        autoFocus
                      />
                    ) : (
                      <h2
                        className={`text-3xl font-bold ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {checklist.title}
                      </h2>
                    )}
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        checklist.type === "conditional"
                          ? "bg-red-100 text-red-700"
                          : "bg-orange-100 text-orange-700"
                      }`}
                    >
                      {checklist.type}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Progress Bar */}
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-32 h-2 rounded-full overflow-hidden ${
                          darkMode ? "bg-white/20" : "bg-gray-200"
                        }`}
                      >
                        <div
                          className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                          style={{ width: `${getProgress(checklist)}%` }}
                        />
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        {Math.round(getProgress(checklist))}%
                      </span>
                    </div>

                    {/* Visibility Button */}
                    <button
                      onClick={() =>
                        openVisibilityDialog(checklist.id, "checklist")
                      }
                      className={`p-2 rounded-lg transition-colors relative ${
                        darkMode
                          ? "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                          : "bg-purple-100 text-purple-600 hover:bg-purple-200"
                      }`}
                      title="Set Visibility"
                    >
                      <Eye className="w-5 h-5" />
                      {checklist.visibleToStores &&
                        checklist.visibleToStores.length > 0 && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></span>
                        )}
                    </button>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleTitleEdit(checklist.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                        }`}
                      >
                        <Edit3 className="w-5 h-5 text-orange-400" />
                      </button>
                      <button
                        onClick={() => duplicateChecklist(checklist)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                        }`}
                      >
                        <Copy className="w-5 h-5 text-blue-400" />
                      </button>
                      <button
                        onClick={() => saveChecklist(checklist)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            : "bg-green-100 text-green-600 hover:bg-green-200"
                        }`}
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteChecklist(checklist.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-red-100 text-red-600 hover:bg-red-200"
                        }`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Visibility Status */}
                {checklist.visibleToStores &&
                  checklist.visibleToStores.length > 0 && (
                    <div className="mb-4 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-purple-500" />
                      <span
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        Visible to:{" "}
                        {checklist.visibleToStores.includes("all")
                          ? "All Stores"
                          : checklist.visibleToStores
                              .map((storeId) => getStoreName(storeId))
                              .join(", ")}
                      </span>
                    </div>
                  )}

                {/* Add Item Input */}
                <div className="mb-6">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newItemText[checklist.id] || ""}
                      onChange={(e) =>
                        setNewItemText({
                          ...newItemText,
                          [checklist.id]: e.target.value,
                        })
                      }
                      placeholder={
                        checklist.type === "conditional"
                          ? "Add a new question..."
                          : "Add a new task..."
                      }
                      className={`flex-1 px-4 py-3 rounded-xl border focus:outline-none focus:border-orange-400 transition-all ${
                        darkMode
                          ? "bg-white/10 border-white/20 text-white placeholder-white/50"
                          : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"
                      }`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          addItem(
                            checklist.id,
                            newItemText[checklist.id] || "",
                            checklist.type === "conditional"
                          );
                        }
                      }}
                    />
                    <button
                      onClick={() =>
                        addItem(
                          checklist.id,
                          newItemText[checklist.id] || "",
                          checklist.type === "conditional"
                        )
                      }
                      className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                        darkMode
                          ? "bg-orange-500 text-white hover:bg-orange-600"
                          : "bg-orange-500 text-white hover:bg-orange-600"
                      }`}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Items List */}
                <div className="space-y-3">
                  {getVisibleItems(checklist).map((item) =>
                    renderItem(checklist, item)
                  )}
                </div>

                {checklist.items.length === 0 && (
                  <div
                    className={`text-center py-12 ${
                      darkMode ? "text-white/50" : "text-gray-400"
                    }`}
                  >
                    <List className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No items yet. Add your first item above!</p>
                  </div>
                )}
              </div>
            ))}

            {/* Active Dynamic Forms */}
            {dynamicForms.map((form) => (
              <div
                key={form.id}
                className={`mb-8 p-8 rounded-2xl border transition-all duration-300 hover:shadow-lg ${
                  darkMode
                    ? "bg-gradient-to-br from-white/5 to-white/10 border-white/20 backdrop-blur-sm"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                {/* Form Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4 flex-1">
                    {form.isEditingTitle ? (
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) =>
                          setDynamicForms((prev) =>
                            prev.map((f) =>
                              f.id === form.id
                                ? { ...f, title: e.target.value }
                                : f
                            )
                          )
                        }
                        onBlur={() => updateFormTitle(form.id, form.title)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            updateFormTitle(form.id, form.title);
                          }
                        }}
                        className={`text-3xl font-bold bg-transparent border-b-2 border-blue-400 focus:outline-none focus:border-red-400 transition-colors ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                        autoFocus
                      />
                    ) : (
                      <h2
                        className={`text-3xl font-bold ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {form.title}
                      </h2>
                    )}
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                      Dynamic Form
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`text-sm font-medium ${
                        darkMode ? "text-white/70" : "text-gray-600"
                      }`}
                    >
                      {form.fields.length} fields
                    </span>

                    {/* Visibility Button */}
                    <button
                      onClick={() => openVisibilityDialog(form.id, "form")}
                      className={`p-2 rounded-lg transition-colors relative ${
                        darkMode
                          ? "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                          : "bg-purple-100 text-purple-600 hover:bg-purple-200"
                      }`}
                      title="Set Visibility"
                    >
                      <Eye className="w-5 h-5" />
                      {form.visibleToStores &&
                        form.visibleToStores.length > 0 && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></span>
                        )}
                    </button>

                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleFormTitleEdit(form.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                        }`}
                      >
                        <Edit3 className="w-5 h-5 text-blue-400" />
                      </button>
                      <button
                        onClick={() => {
                          setCurrentFormId(form.id);
                          setShowFieldTypeDialog(true);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                            : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                        }`}
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => saveFormToDatabase(form)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            : "bg-green-100 text-green-600 hover:bg-green-200"
                        }`}
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteForm(form.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-red-100 text-red-600 hover:bg-red-200"
                        }`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Visibility Status */}
                {form.visibleToStores && form.visibleToStores.length > 0 && (
                  <div className="mb-4 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-purple-500" />
                    <span
                      className={`text-sm ${
                        darkMode ? "text-white/70" : "text-gray-600"
                      }`}
                    >
                      Visible to:{" "}
                      {form.visibleToStores.includes("all")
                        ? "All Stores"
                        : form.visibleToStores
                            .map((storeId) => getStoreName(storeId))
                            .join(", ")}
                    </span>
                  </div>
                )}

                {/* Form Fields */}
                <div className="space-y-4">
                  {form.fields.map((field) => renderFormField(form, field))}
                </div>

                {form.fields.length === 0 && (
                  <div
                    className={`text-center py-12 ${
                      darkMode ? "text-white/50" : "text-gray-400"
                    }`}
                  >
                    <FormInput className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No fields yet. Add your first field!</p>
                    <button
                      onClick={() => {
                        setCurrentFormId(form.id);
                        setShowFieldTypeDialog(true);
                      }}
                      className={`mt-4 px-4 py-2 rounded-lg transition-colors ${
                        darkMode
                          ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                          : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                      }`}
                    >
                      Add Field
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Empty State for Creation View */}
            {checklists.length === 0 && dynamicForms.length === 0 && (
              <div
                className={`text-center py-16 ${
                  darkMode ? "text-white/50" : "text-gray-400"
                }`}
              >
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto mb-6 opacity-20">
                    <List className="w-full h-full" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Something went wrong
                  </h3>
                  <p className="text-lg">
                    No active items to display. Go back to home.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Dialog Overlay */}
        {showDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`p-8 rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-w-md w-full mx-4 ${
                darkMode
                  ? "bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20"
                  : "bg-white border border-gray-200"
              }`}
            >
              <div className="text-center mb-6">
                <h2
                  className={`text-2xl font-bold mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Which type of checklist would you like to create?
                </h2>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white/70" : "text-gray-600"
                  }`}
                >
                  Choose the type that best fits your needs
                </p>
              </div>

              <div className="space-y-4">
                {/* Normal Checklist Option */}
                <button
                  onClick={() => createNewChecklist("normal")}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                    darkMode
                      ? "border-white/20 hover:border-orange-400 bg-white/5 hover:bg-white/10"
                      : "border-gray-200 hover:border-orange-400 bg-gray-50 hover:bg-orange-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-lg ${
                        darkMode ? "bg-orange-500/20" : "bg-orange-100"
                      }`}
                    >
                      <List className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="text-left">
                      <h3
                        className={`font-semibold text-lg ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Normal Checklist
                      </h3>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        Simple checklist with check/uncheck items
                      </p>
                    </div>
                  </div>
                </button>

                {/* Conditional Checklist Option */}
                <button
                  onClick={() => createNewChecklist("conditional")}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                    darkMode
                      ? "border-white/20 hover:border-orange-400 bg-white/5 hover:bg-white/10"
                      : "border-gray-200 hover:border-orange-400 bg-gray-50 hover:bg-orange-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-lg ${
                        darkMode ? "bg-red-500/20" : "bg-red-100"
                      }`}
                    >
                      <GitBranch className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="text-left">
                      <h3
                        className={`font-semibold text-lg ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Conditional Checklist
                      </h3>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        Yes/No questions with conditional sub-tasks
                      </p>
                    </div>
                  </div>
                </button>

                {/* Dynamic Form Option */}
                <button
                  onClick={() => createNewChecklist("dynamic")}
                  className={`w-full p-6 rounded-xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                    darkMode
                      ? "border-white/20 hover:border-orange-400 bg-white/5 hover:bg-white/10"
                      : "border-gray-200 hover:border-orange-400 bg-gray-50 hover:bg-orange-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-lg ${
                        darkMode ? "bg-blue-500/20" : "bg-blue-100"
                      }`}
                    >
                      <FormInput className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="text-left">
                      <h3
                        className={`font-semibold text-lg ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Dynamic Form
                      </h3>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        Create forms with various field types
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowDialog(false)}
                className={`w-full mt-6 p-3 rounded-xl border transition-all duration-300 ${
                  darkMode
                    ? "border-white/20 text-white/70 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Field Type Selection Dialog */}
        {showFieldTypeDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`p-8 rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-w-lg w-full mx-4 ${
                darkMode
                  ? "bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20"
                  : "bg-white border border-gray-200"
              }`}
            >
              <div className="text-center mb-6">
                <h2
                  className={`text-2xl font-bold mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Choose Field Type
                </h2>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white/70" : "text-gray-600"
                  }`}
                >
                  Select the type of field you want to add
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { type: "text", label: "Text Input", icon: Type },
                  { type: "paragraph", label: "Paragraph", icon: FileText },
                  {
                    type: "multiple-choice",
                    label: "Multiple Choice",
                    icon: Circle,
                  },
                  {
                    type: "checkboxes",
                    label: "Checkboxes",
                    icon: CheckSquare,
                  },
                  { type: "dropdown", label: "Dropdown", icon: ChevronDown },
                  { type: "date", label: "Date", icon: Calendar },
                  { type: "time", label: "Time", icon: Clock },
                  { type: "rating", label: "Rating", icon: Star },
                ].map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() =>
                      addField(currentFormId, type as FormField["type"])
                    }
                    className={`p-4 rounded-xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                      darkMode
                        ? "border-white/20 hover:border-orange-400 bg-white/5 hover:bg-white/10"
                        : "border-gray-200 hover:border-orange-400 bg-gray-50 hover:bg-orange-50"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Icon className="w-6 h-6 text-orange-500" />
                      <span
                        className={`text-sm font-medium ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  setShowFieldTypeDialog(false);
                  setCurrentFormId("");
                }}
                className={`w-full mt-6 p-3 rounded-xl border transition-all duration-300 ${
                  darkMode
                    ? "border-white/20 text-white/70 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Visibility Settings Dialog */}
        {showVisibilityDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`p-8 rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-w-md w-full mx-4 ${
                darkMode
                  ? "bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20"
                  : "bg-white border border-gray-200"
              }`}
            >
              <div className="text-center mb-6">
                <h2
                  className={`text-2xl font-bold mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Set Visibility
                </h2>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white/70" : "text-gray-600"
                  }`}
                >
                  Choose which stores can see this {currentVisibilityType}
                </p>
              </div>

              <div className="space-y-3 max-h-60 overflow-y-auto">
                {getAvailableStores().map((store) => {
                  const currentStores = getCurrentVisibilityStores();
                  const isSelected = currentStores.includes(
                    store.store_id.toString()
                  );

                  return (
                    <label
                      key={store.store_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-300 cursor-pointer ${
                        isSelected
                          ? darkMode
                            ? "border-purple-400 bg-purple-500/20"
                            : "border-purple-400 bg-purple-50"
                          : darkMode
                          ? "border-white/20 hover:border-white/40 bg-white/5"
                          : "border-gray-200 hover:border-gray-300 bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const currentStores = getCurrentVisibilityStores();
                          const storeId = store.store_id.toString();
                          let newStores;

                          if (e.target.checked) {
                            newStores = [...currentStores, storeId];
                          } else {
                            newStores = currentStores.filter(
                              (id) => id !== storeId
                            );
                          }

                          updateVisibility(newStores);
                        }}
                        className="rounded"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div
                          className={`p-2 rounded-lg ${
                            darkMode ? "bg-blue-500/20" : "bg-blue-100"
                          }`}
                        >
                          {store.store_id === "all" ? (
                            <Users className="w-4 h-4 text-blue-500" />
                          ) : (
                            <Settings className="w-4 h-4 text-blue-500" />
                          )}
                        </div>
                        <span
                          className={`font-medium ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {store.name}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowVisibilityDialog(false);
                    setCurrentVisibilityId("");
                  }}
                  className={`flex-1 p-3 rounded-xl border transition-all duration-300 ${
                    darkMode
                      ? "border-white/20 text-white/70 hover:bg-white/10"
                      : "border-gray-300 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Clear all visibility settings
                    updateVisibility([]);
                  }}
                  className={`flex-1 p-3 rounded-xl transition-all duration-300 ${
                    darkMode
                      ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      : "bg-red-100 text-red-600 hover:bg-red-200"
                  }`}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
